import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AeroStatus } from '../../../components/Aero/AeroHomeParts';
import { sessionDisplayName } from '../insights/insightsModel';
import '../../../styles/userTesting/index.css';
import '../../../styles/aero-ut-theme.css';
import '../UserTestingV3.css';
import { deviceShort, tasksDone, transcriptState } from '../review/reviewModel';

function UTSessionRow({
  session,
  sessionNumber = null,
  projectId,
  tasks = [],
  selected = false,
  isNew = false,
  onSelect,
  onDelete
}) {
  const navigate = useNavigate();

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const duration = session.completedAt && session.startedAt
    ? Math.floor((new Date(session.completedAt) - new Date(session.startedAt)) / 1000)
    : 0;

  // 'abandoned' is written by cleanupStaleTestingSessions for sessions whose
  // tab died mid-recording; without its own case it fell through to the
  // 'In progress' label.
  const statusLabel = session.status === 'completed'
    ? 'Completed'
    : session.status === 'incomplete'
      ? 'Incomplete'
      : session.status === 'abandoned'
        ? 'Abandoned'
        : 'In progress';

  // v3 status word kinds: done (green), part done (draft ring), abandoned
  // (closed), recording (new, the blue dot).
  const statusKind = session.status === 'completed'
    ? 'live'
    : session.status === 'incomplete'
      ? 'draft'
      : session.status === 'abandoned'
        ? 'closed'
        : 'new';

  // Derive tags from analysis state.
  const analysis = session.analysis;
  const frictionCount = analysis?.friction_points?.length || 0;
  const analysisDone = session.analysisStatus === 'completed';
  const tags = [];
  if (session.status === 'incomplete') {
    tags.push({ label: 'Abandoned', kind: 'issue' });
  }
  if (analysisDone) {
    if (frictionCount > 0) {
      tags.push({ label: 'Issue', kind: 'issue' });
    } else if (session.status === 'completed') {
      tags.push({ label: 'Works well', kind: 'good' });
    }
  }

  // Short snippet: first sentence of the analysis summary, trimmed to ~120 chars.
  let snippet = null;
  if (analysis?.summary) {
    const text = String(analysis.summary).trim();
    const firstSentence = text.split(/(?<=[.!?])\s/)[0] || text;
    snippet = firstSentence.length > 120
      ? `${firstSentence.slice(0, 117)}…`
      : firstSentence;
  }

  const role = session.testerRole || session.source || null;

  // v3 phase 5b: the device and tasks done on every row, and the transcript's
  // state in words when there is no summary line yet.
  const device = deviceShort(session);
  const done = tasksDone(session, tasks);
  const hasWords = Array.isArray(session.transcript) && session.transcript.length > 0;
  const tState = snippet ? null : transcriptState(session, hasWords ? [true] : []);
  const stateLine = tState === 'arriving'
    ? 'Transcript arriving, usually about a minute. The recording is ready to watch now.'
    : tState === 'noaudio'
      ? 'No audio. The microphone was off; the tasks still count.'
      : tState === 'silent'
        ? 'Nothing was said, so the transcript is empty. The tasks still count.'
      : tState === 'failed'
        ? "The transcript didn't finish. The recording is safe."
        : null;

  const handleRowClick = (e) => {
    if (e.target.closest('.ut-session-checkbox') || e.target.closest('[data-row-action]')) {
      return;
    }
    navigate(`/user-testing/${projectId}/sessions/${session.id}`);
  };

  const handleCheckboxClick = (e) => {
    // A label click also fires a second click on its input; without this the
    // toggle ran twice and cancelled itself.
    e.preventDefault();
    e.stopPropagation();
    onSelect?.(session.id);
  };

  // Name rule (insightsModel.sessionDisplayName): tester's name, "You" for
  // an owner preview, else "Anonymous tester"; "Session #N" alongside.
  const testerName = sessionDisplayName(session);
  const initial = testerName.charAt(0).toUpperCase();

  return (
    <div className="aero-session-row aero-ses-v3" onClick={handleRowClick}>
      <label className="ut-session-checkbox ut-checkbox" onClick={handleCheckboxClick}>
        <input type="checkbox" checked={selected} onChange={() => {}} />
        <span className="ut-checkbox-box">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </span>
      </label>

      <div className="aero-ses-who">
        <span className="aero-ses-av" aria-hidden="true">{initial}</span>
        <div className="aero-ses-body">
          <div className="aero-ses-line">
            <span className="aero-ses-name">{testerName}</span>
            {isNew && <span className="ut-session-new">New</span>}
            {sessionNumber && <span className="aero-ses-role">Session #{sessionNumber}</span>}
            {role && <span className="aero-ses-role">{role}</span>}
            {tags.map((t) => (
              <span key={t.label} className={`aero-tagw aero-tagw-${t.kind}`}>{t.label}</span>
            ))}
          </div>
          {snippet && <div className="aero-ses-quote">{snippet}</div>}
          {!snippet && stateLine && <div className="aero-ses-quote aero-ses-state">{stateLine}</div>}
          {(device || done) && (
            <div className="aero-ses-facts">
              {device && <span>{device}</span>}
              {done && <span>Tasks {done.done} of {done.total}</span>}
            </div>
          )}
        </div>
      </div>

      <div className="aero-ses-meta">
        <span className="aero-ses-dur">{formatDuration(duration)}</span>
        <AeroStatus kind={statusKind}>{statusLabel}</AeroStatus>
      </div>

      <div className="aero-session-row-actions" data-row-action>
        {session.audioUrl && (
          <button
            type="button"
            className="aero-btn aero-btn-ghost aero-btn-icon"
            title="Play audio"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/user-testing/${projectId}/sessions/${session.id}`);
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polygon points="5 3 19 12 5 21 5 3" />
            </svg>
          </button>
        )}
        <button
          type="button"
          className="aero-btn aero-btn-ghost aero-btn-icon"
          title="Delete session"
          onClick={(e) => {
            e.stopPropagation();
            onDelete?.(session.id);
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
          </svg>
        </button>
      </div>
    </div>
  );
}

export default UTSessionRow;
